import { classroom_v1 } from "@googleapis/classroom";

import { GoogleOAuthClient } from "@/services/GoogleOAuth";
import {
  ClassroomAttachment,
  classroomAttachmentSchema,
  CourseListItem,
  CreateAnnouncementAttachmentArgs,
  CreateCourseWorkArgs,
  courseWorkCreatedResultSchema,
  CourseWorkCreatedResult,
} from "@/types/services";

export type GetAddOnContextArgs = {
  courseId: string;
  itemId: string;
  attachmentId: string;
};

export type SubmitPupilResponseArgs = {
  courseId: string;
  itemId: string;
  attachmentId: string;
  submissionId: string;
  pointsEarned: number;
};

export type FetchPupilResponseArgs = {
  courseId: string;
  itemId: string;
  attachmentId: string;
  submissionId: string;
};
// These enums can be expanded in future if required
export enum CourseState {
  ACTIVE = "ACTIVE",
}

export enum CourseWorkState {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
}

export enum CourseWorkType {
  ASSIGNMENT = "ASSIGNMENT",
}

export type ClassroomApi = classroom_v1.Classroom;

export class GoogleClassroomClient {
  constructor(
    private readonly baseUrl: string,
    private readonly oAuthClient: GoogleOAuthClient,
  ) {
    this.baseUrl = baseUrl;
    this.oAuthClient = oAuthClient;
  }

  private async getClassroomClient(): Promise<ClassroomApi> {
    const auth2Client = await this.oAuthClient.getOAuth2Client();
    const classroom = new classroom_v1.Classroom({
      auth: auth2Client,
    });
    return classroom;
  }

  /**
   * Constructs the full URL for a given path, will automatically append Vercel bypass query parameters if a bypass
   * secret is set and the environment is staging
   * @param path - the path to append to the base URL, should start with a "/"
   */
  private getUrl(path: string): string {
    const vercelBypassSecret = process.env.VERCEL_AUTOMATION_BYPASS_SECRET;
    const isStaging = process.env.VERCEL_ENV !== "production";
    if (vercelBypassSecret && isStaging) {
      const bypassQueryParam = `x-vercel-protection-bypass=${vercelBypassSecret}&x-vercel-set-bypass-cookie=samesitenone`;
      return `${this.baseUrl}${path}?${bypassQueryParam}`;
    }
    return `${this.baseUrl}${path}`;
  }

  private getLessonPath(
    programmeSlug: string,
    unitSlug: string,
    lessonSlug: string,
  ): string {
    return `/pupils/programmes/${programmeSlug}/units/${unitSlug}/lessons/${lessonSlug}`;
  }

  private getClassroomLessonPath(
    programmeSlug: string,
    unitSlug: string,
    lessonSlug: string,
  ): string {
    return `/classroom/pupil/programmes/${programmeSlug}/${unitSlug}/${lessonSlug}`;
  }

  private getClassroomPrintableResultsPath(
    programmeSlug: string,
    unitSlug: string,
    lessonSlug: string,
  ): string {
    return `${this.getClassroomLessonPath(programmeSlug, unitSlug, lessonSlug)}/results/printable`;
  }

  public async getAddOnContext(
    args: GetAddOnContextArgs,
  ): Promise<classroom_v1.Schema$AddOnContext> {
    const classroom = await this.getClassroomClient();
    const res = await classroom.courses.courseWork.getAddOnContext({
      courseId: args.courseId,
      itemId: args.itemId,
      attachmentId: args.attachmentId,
    });
    return res.data;
  }

  public async createAnnouncementAttachment(
    args: CreateAnnouncementAttachmentArgs,
  ): Promise<ClassroomAttachment> {
    const {
      courseId,
      itemId,
      addOnToken,
      title,
      lessonSlug,
      programmeSlug,
      unitSlug,
      maxPoints,
    } = args;
    const classroom = await this.getClassroomClient();
    const gradebookArgs = maxPoints
      ? {
          maxPoints,
          studentWorkReviewUri: {
            uri: this.getUrl(
              this.getClassroomPrintableResultsPath(
                programmeSlug,
                unitSlug,
                lessonSlug,
              ),
            ),
          },
        }
      : {};
    const attachment =
      await classroom.courses.courseWork.addOnAttachments.create({
        courseId,
        itemId,
        addOnToken,
        requestBody: {
          title,
          teacherViewUri: {
            uri: this.getUrl(
              this.getLessonPath(programmeSlug, unitSlug, lessonSlug),
            ),
          },
          studentViewUri: {
            uri: this.getUrl(
              this.getClassroomLessonPath(programmeSlug, unitSlug, lessonSlug),
            ),
          },
          ...gradebookArgs,
        },
      });

    const parsed = classroomAttachmentSchema.parse(attachment.data);

    const gradebookData = parsed.maxPoints
      ? {
          maxPoints: parsed.maxPoints,
          studentWorkReviewUri: parsed.studentWorkReviewUri,
        }
      : {};

    return {
      courseId: parsed.courseId,
      postId: parsed.postId,
      id: parsed.id,
      title: parsed.title,
      teacherViewUri: parsed.teacherViewUri,
      studentViewUri: parsed.studentViewUri,
      itemId: parsed.itemId,
      ...gradebookData,
    };
  }

  public async submitPupilResponse(
    args: SubmitPupilResponseArgs,
  ): Promise<classroom_v1.Schema$AddOnAttachmentStudentSubmission> {
    const classroom = await this.getClassroomClient();
    const res =
      await classroom.courses.courseWork.addOnAttachments.studentSubmissions.patch(
        {
          courseId: args.courseId,
          itemId: args.itemId,
          attachmentId: args.attachmentId,
          submissionId: args.submissionId,
          updateMask: "pointsEarned",
          requestBody: { pointsEarned: args.pointsEarned },
        },
      );
    return res.data;
  }

  public async fetchPupilResponse(
    args: FetchPupilResponseArgs,
  ): Promise<classroom_v1.Schema$AddOnAttachmentStudentSubmission> {
    const classroom = await this.getClassroomClient();
    const res =
      await classroom.courses.courseWork.addOnAttachments.studentSubmissions.get(
        {
          courseId: args.courseId,
          itemId: args.itemId,
          attachmentId: args.attachmentId,
          submissionId: args.submissionId,
        },
      );
    return res.data;
  }

  /**
   * Lists the active courses for the authenticated teacher.
   */
  public async listCourses(pageSize = 30): Promise<CourseListItem[]> {
    const classroom = await this.getClassroomClient();
    const res = await classroom.courses.list({
      teacherId: "me",
      courseStates: [CourseState.ACTIVE],
      pageSize,
    });
    const courses = res.data.courses ?? [];
    // Filter out any courses that are missing an id or name, as those are required for our use case. We can be lenient with section as it's only used for display purposes and not all courses have to have it.
    return courses
      .filter((c) => c.id && c.name)
      .map((c) => ({
        id: c.id!,
        name: c.name!,
        section: c.section ?? undefined,
      }));
  }

  /**
   * Creates a new CourseWork assignment in a course with a Link material.
   * Creates as DRAFT so the teacher can review before publishing.
   */
  public async createCourseWork(
    courseId: string,
    args: CreateCourseWorkArgs,
  ): Promise<CourseWorkCreatedResult> {
    const classroom = await this.getClassroomClient();
    const res = await classroom.courses.courseWork.create({
      courseId,
      requestBody: {
        title: args.title,
        description: args.description,
        workType: CourseWorkType.ASSIGNMENT,
        state: CourseWorkState.DRAFT,
        maxPoints: args.maxPoints,
        materials: [{ link: { url: args.lessonUrl, title: args.title } }],
      },
    });
    return courseWorkCreatedResultSchema.parse({
      courseWorkId: res.data.id,
      courseId: res.data.courseId,
      alternateLink: res.data.alternateLink,
      title: res.data.title,
    });
  }

  /**
   * Returns the pupil's own StudentSubmission for a given courseWork item.
   * Throws if no submission is found (fail closed).
   * Must be called with the pupil's OAuth client.
   */
  public async getStudentSubmission(
    courseId: string,
    courseWorkId: string,
  ): Promise<classroom_v1.Schema$StudentSubmission> {
    const classroom = await this.getClassroomClient();
    const res = await classroom.courses.courseWork.studentSubmissions.list({
      courseId,
      courseWorkId,
      userId: "me",
      pageSize: 1,
    });
    const submission = res.data.studentSubmissions?.[0];
    if (!submission) {
      throw new Error(
        `No student submission found for courseWorkId=${courseWorkId} in courseId=${courseId}`,
      );
    }
    return submission;
  }

  /**
   * Turns in a student submission on behalf of the student.
   * Must be called with the student's OAuth client.
   */
  public async turnInStudentSubmission(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
  ): Promise<void> {
    const classroom = await this.getClassroomClient();
    await classroom.courses.courseWork.studentSubmissions.turnIn({
      courseId,
      courseWorkId,
      id: submissionId,
    });
  }

  /**
   * Adds a link attachment to a student submission on behalf of the student.
   * Must be called with the student's OAuth client.
   * Must be called BEFORE turnInStudentSubmission — Google does not allow
   * modifying attachments on a TURNED_IN submission.
   */
  public async addSubmissionLink(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
    url: string,
  ): Promise<void> {
    const classroom = await this.getClassroomClient();
    await classroom.courses.courseWork.studentSubmissions.modifyAttachments({
      courseId,
      courseWorkId,
      id: submissionId,
      requestBody: {
        addAttachments: [{ link: { url } }],
      },
    });
  }

  /**
   * Fetches a specific student submission by its ID.
   * Must be called with the teacher's OAuth client — teachers can fetch any
   * student's submission directly by ID without a userId filter.
   */
  public async getStudentSubmissionById(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
  ): Promise<classroom_v1.Schema$StudentSubmission> {
    const classroom = await this.getClassroomClient();
    const res = await classroom.courses.courseWork.studentSubmissions.get({
      courseId,
      courseWorkId,
      id: submissionId,
    });
    return res.data;
  }

  /**
   * Patches a student submission's grade.
   * Both draftGrade and assignedGrade must be set together — Google requires
   * draftGrade to exist before assignedGrade can be set.
   * Must be called with the teacher's OAuth client.
   */
  public async patchStudentSubmission(
    courseId: string,
    courseWorkId: string,
    submissionId: string,
    draftGrade: number,
    assignedGrade: number,
  ): Promise<void> {
    const classroom = await this.getClassroomClient();
    await classroom.courses.courseWork.studentSubmissions.patch({
      courseId,
      courseWorkId,
      id: submissionId,
      updateMask: "draftGrade,assignedGrade",
      requestBody: { draftGrade, assignedGrade },
    });
  }
}
